// Client-side password GUIDANCE for the accept-invite screen (spec 15 §3.1.2).
//
// THE SERVER IS THE AUTHORITY, always. It enforces min 12 characters AND
// zxcvbn ≥ 3 (`staff-password-policy.ts`), and a rejection there is rendered
// verbatim by the screen. This module exists only so the person choosing a
// password gets a hint WHILE they type instead of after a round trip.
//
// It is deliberately NOT zxcvbn: shipping zxcvbn's dictionaries into the
// browser bundle would cost ~400 KB to duplicate a check the server already
// makes, and a client-side score that disagreed with the server's would be
// worse than none — the user would see "Strong" and be refused. So this is a
// transparent structural estimate, labelled as a guide on the screen, and it
// NEVER blocks submission: only the two things the client can know for certain
// (the 12-character floor, and whether the two fields match) gate the button.

export type PasswordStrength = "none" | "weak" | "fair" | "good" | "strong";

/** The floor the API enforces (STAFF_PASSWORD_MIN_LENGTH). */
export const PASSWORD_MIN_LENGTH = 12;

/**
 * Distinct character classes present: lower, upper, digit, symbol.
 *
 * WHITESPACE IS NOT A CLASS. It is a separator, and counting it as one made
 * every passphrase claim a second class for free — "correct horse battery"
 * scored as though it mixed symbols in, and the meter said "Strong" for
 * `aaaa aaaa aaaa aaaa aaaa`, which zxcvbn scores at the floor. A guide that
 * flatters is worse than no guide: it earns the user a server refusal after
 * they were told they had done well.
 */
function classesIn(password: string): number {
  let classes = 0;
  if (/[a-z]/u.test(password)) classes++;
  if (/[A-Z]/u.test(password)) classes++;
  if (/[0-9]/u.test(password)) classes++;
  if (/[^A-Za-z0-9\s]/u.test(password)) classes++;
  return classes;
}

/**
 * DISTINCT words of more than two letters.
 *
 * Distinct, because a repeated word carries no more entropy than one word:
 * `aaaa aaaa aaaa aaaa` is not a four-word passphrase, and counting it as one
 * was the other half of the same flattery.
 */
function distinctWordsIn(password: string): number {
  const words = password
    .trim()
    .toLowerCase()
    .split(/\s+/u)
    .filter((w) => w.length > 2);
  return new Set(words).size;
}

/** A single repeated character, or a short run repeated to length ("abcabcabc"). */
function isRepetitive(password: string): boolean {
  if (/^(.)\1*$/u.test(password)) return true;
  for (let unit = 1; unit <= Math.floor(password.length / 2); unit++) {
    const head = password.slice(0, unit);
    if (password.length % unit === 0 && head.repeat(password.length / unit) === password) {
      return true;
    }
  }
  return false;
}

/** A straight run through the alphabet or the digits ("abcdefghijkl", "123456789012"). */
function isSequential(password: string): boolean {
  if (password.length < 4) return false;
  const lower = password.toLowerCase();
  let ascending = true;
  let descending = true;
  for (let i = 1; i < lower.length; i++) {
    const delta = (lower.codePointAt(i) ?? 0) - (lower.codePointAt(i - 1) ?? 0);
    if (delta !== 1) ascending = false;
    if (delta !== -1) descending = false;
  }
  return ascending || descending;
}

/**
 * A structural strength estimate for the meter. Words, not a percentage — a
 * number would imply a precision this cannot have.
 */
export function estimateStrength(password: string): PasswordStrength {
  if (password.length === 0) return "none";
  if (isRepetitive(password) || isSequential(password)) return "weak";
  if (password.length < PASSWORD_MIN_LENGTH) return "weak";

  const classes = classesIn(password);
  const words = distinctWordsIn(password);
  // A passphrase earns its length: four+ words, or 16+ characters with variety.
  if (password.length >= 20 && (classes >= 2 || words >= 4)) return "strong";
  if (password.length >= 16 && (classes >= 2 || words >= 3)) return "good";
  if (password.length >= PASSWORD_MIN_LENGTH && classes >= 2) return "fair";
  return "weak";
}

/** Meter fill, 0–4 — a visual echo of the word, not a second claim. */
export function strengthSteps(strength: PasswordStrength): number {
  switch (strength) {
    case "none":
      return 0;
    case "weak":
      return 1;
    case "fair":
      return 2;
    case "good":
      return 3;
    case "strong":
      return 4;
  }
}
